import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  ArrowLeft, FileText, Download, Send, Loader2, Mail, Phone, MapPin, Building2, Hash, Truck,
  CheckCircle2, Clock, Eye, AlertCircle,
} from "lucide-react";
import { toast } from "sonner";
import ClienteFrotaPanel from "../components/ClienteFrotaPanel";

const STATUS_LABEL = {
  rascunho: { label: "Rascunho", cor: "bg-slate-100 text-slate-700" },
  gerada: { label: "PDF Gerado", cor: "bg-blue-100 text-blue-700" },
  enviada: { label: "Enviada", cor: "bg-amber-100 text-amber-800" },
  visualizada: { label: "Visualizada", cor: "bg-cyan-100 text-cyan-800" },
  assinada: { label: "Assinada", cor: "bg-emerald-100 text-emerald-700" },
  aprovada: { label: "Aprovada", cor: "bg-emerald-100 text-emerald-700" },
  recusada: { label: "Recusada", cor: "bg-red-100 text-red-700" },
};

const fmtBRL = (v) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(Number(v) || 0);

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("pt-BR");
  } catch {
    return "—";
  }
};

export default function ClienteCadastroDetalhe() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [cliente, setCliente] = useState(null);
  const [propostas, setPropostas] = useState([]);
  const [loadingCli, setLoadingCli] = useState(true);
  const [loadingProp, setLoadingProp] = useState(true);
  const [reenviando, setReenviando] = useState(null);

  useEffect(() => {
    setLoadingCli(true);
    api
      .get(`/clientes/${id}`)
      .then(({ data }) => setCliente(data))
      .catch(() => {
        toast.error("Cliente não encontrado");
        navigate("/app/clientes");
      })
      .finally(() => setLoadingCli(false));

    setLoadingProp(true);
    api
      .get(`/clientes/${id}/propostas`)
      .then(({ data }) => setPropostas(data.items || []))
      .catch(() => setPropostas([]))
      .finally(() => setLoadingProp(false));
  }, [id, navigate]);

  const baixarPDF = (pid, numero) => {
    const link = document.createElement("a");
    const apiBase = process.env.REACT_APP_BACKEND_URL || "";
    link.href = `${apiBase}/api/proposta-nativa/${pid}/pdf`;
    link.target = "_blank";
    link.click();
    toast.success(`Baixando proposta ${numero}…`);
  };

  const reenviarProposta = async (pid, numero) => {
    setReenviando(pid);
    try {
      await api.post(`/proposta-nativa/${pid}/enviar`);
      toast.success(`Proposta ${numero} reenviada por email`);
      // recarrega para refletir novo status
      const { data } = await api.get(`/clientes/${id}/propostas`);
      setPropostas(data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao reenviar");
    } finally {
      setReenviando(null);
    }
  };

  if (loadingCli) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-zinc-400" />
      </div>
    );
  }
  if (!cliente) return null;

  return (
    <div className="space-y-5" data-testid="page-cliente-cadastro-detalhe">
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate("/app/clientes")}
          data-testid="back-clientes"
        >
          <ArrowLeft className="w-4 h-4 mr-1" /> Clientes
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold tracking-tight">{cliente.nome}</h1>
          <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
            <span className="font-mono">{cliente.cnpj_cpf || "—"}</span>
            <Badge variant="outline">{cliente.tipo || "PJ"}</Badge>
            {cliente.unidade_id && (
              <Badge variant="outline" className="flex items-center gap-1">
                <Building2 size={10} /> {cliente.unidade_id.replace("un_", "")}
              </Badge>
            )}
            <Badge>{cliente.status || "em_analise"}</Badge>
          </div>
        </div>
      </div>

      <Tabs defaultValue="detalhes">
        <TabsList data-testid="cliente-detalhe-tabs">
          <TabsTrigger value="detalhes" data-testid="tab-detalhes">
            Detalhes
          </TabsTrigger>
          <TabsTrigger value="frota" data-testid="tab-frota">
            <Truck size={14} className="mr-1" />
            Frota
          </TabsTrigger>
          <TabsTrigger value="propostas" data-testid="tab-propostas">
            <FileText size={14} className="mr-1" />
            Propostas
            {propostas.length > 0 && (
              <Badge className="ml-2 h-5 px-2 text-[10px]" variant="secondary">
                {propostas.length}
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="frota" className="mt-4">
          <ClienteFrotaPanel clienteId={id} />
        </TabsContent>

        <TabsContent value="detalhes" className="mt-4">
          <div className="grid md:grid-cols-2 gap-4">
            <Card className="p-5 space-y-3">
              <h3 className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">
                Contato
              </h3>
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2">
                  <Mail size={14} className="text-muted-foreground" />
                  {cliente.email || <span className="text-muted-foreground">—</span>}
                </div>
                <div className="flex items-center gap-2">
                  <Phone size={14} className="text-muted-foreground" />
                  {cliente.telefone || <span className="text-muted-foreground">—</span>}
                </div>
                <div className="flex items-start gap-2">
                  <MapPin size={14} className="text-muted-foreground mt-0.5" />
                  <span>{cliente.endereco || <span className="text-muted-foreground">—</span>}</span>
                </div>
              </div>
            </Card>

            <Card className="p-5 space-y-3">
              <h3 className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">
                Cadastro
              </h3>
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2">
                  <Hash size={14} className="text-muted-foreground" />
                  <span className="font-mono text-xs">{cliente.id}</span>
                </div>
                <div>
                  <span className="text-xs text-muted-foreground">Origem: </span>
                  {cliente.origem || "—"}
                </div>
                <div>
                  <span className="text-xs text-muted-foreground">Criado em: </span>
                  {fmtDate(cliente.criado_em)}
                </div>
                {cliente.parecer_ia && (
                  <div className="pt-2 border-t">
                    <span className="text-xs text-muted-foreground">Parecer IA: </span>
                    <Badge variant="outline">{cliente.parecer_ia}</Badge>
                  </div>
                )}
              </div>
            </Card>

            {cliente.socios && (
              <Card className="p-5 md:col-span-2">
                <h3 className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-2">
                  Sócios
                </h3>
                <p className="text-sm whitespace-pre-wrap">{cliente.socios}</p>
              </Card>
            )}
            {cliente.referencias && (
              <Card className="p-5 md:col-span-2">
                <h3 className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-2">
                  Referências
                </h3>
                <p className="text-sm whitespace-pre-wrap">{cliente.referencias}</p>
              </Card>
            )}
          </div>
        </TabsContent>

        <TabsContent value="propostas" className="mt-4">
          <Card>
            <div className="px-4 py-3 border-b flex items-center justify-between">
              <div>
                <h3 className="text-sm font-semibold">Histórico de propostas</h3>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Todos os PDFs gerados, enviados e assinados para este cliente.
                </p>
              </div>
              {!loadingProp && propostas.length === 0 && (
                <Badge variant="outline" className="text-xs">
                  Nenhuma proposta ainda
                </Badge>
              )}
            </div>
            {loadingProp ? (
              <div className="py-10 text-center text-sm text-muted-foreground">
                <Loader2 className="inline animate-spin mr-2" size={14} />
                Carregando…
              </div>
            ) : propostas.length === 0 ? (
              <div className="py-10 text-center text-sm text-muted-foreground" data-testid="propostas-vazias">
                <AlertCircle className="inline mr-2" size={14} />
                Este cliente ainda não tem propostas. Use o botão "Nova proposta" no painel de propostas.
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-28">Número</TableHead>
                    <TableHead>Data</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Valor</TableHead>
                    <TableHead>Vendedor</TableHead>
                    <TableHead className="w-24 text-center">Aberturas</TableHead>
                    <TableHead className="w-56 text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {propostas.map((p) => {
                    const st = STATUS_LABEL[p.status] || { label: p.status, cor: "bg-slate-100 text-slate-700" };
                    return (
                      <TableRow key={p.id} data-testid={`proposta-row-${p.id}`}>
                        <TableCell className="font-mono text-xs">{p.numero || p.id.slice(0, 8)}</TableCell>
                        <TableCell className="text-xs">
                          <div>{fmtDate(p.criado_em)}</div>
                          {p.assinada_em && (
                            <div className="text-emerald-600 text-[10px] flex items-center gap-1 mt-0.5">
                              <CheckCircle2 size={10} />
                              Assinada {fmtDate(p.assinada_em)}
                            </div>
                          )}
                          {p.enviada_em && !p.assinada_em && (
                            <div className="text-amber-600 text-[10px] flex items-center gap-1 mt-0.5">
                              <Clock size={10} />
                              Enviada {fmtDate(p.enviada_em)}
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge className={st.cor}>{st.label}</Badge>
                        </TableCell>
                        <TableCell className="text-right font-mono text-xs">
                          {fmtBRL(p.valor_total_geral)}
                        </TableCell>
                        <TableCell className="text-xs">{p.vendedor_nome || "—"}</TableCell>
                        <TableCell className="text-center">
                          {p.opens_count ? (
                            <Badge variant="outline" className="text-[10px]">
                              <Eye size={10} className="mr-1" />
                              {p.opens_count}x
                            </Badge>
                          ) : (
                            <span className="text-xs text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex gap-1 justify-end">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => baixarPDF(p.id, p.numero)}
                              data-testid={`baixar-${p.id}`}
                              title="Baixar PDF"
                            >
                              <Download size={12} />
                            </Button>
                            {p.status !== "assinada" && p.status !== "aprovada" && p.cliente_email && (
                              <Button
                                size="sm"
                                variant="outline"
                                disabled={reenviando === p.id}
                                onClick={() => reenviarProposta(p.id, p.numero)}
                                data-testid={`reenviar-${p.id}`}
                                title="Reenviar por email"
                              >
                                {reenviando === p.id ? (
                                  <Loader2 size={12} className="animate-spin" />
                                ) : (
                                  <Send size={12} />
                                )}
                              </Button>
                            )}
                            {p.lead_id && (
                              <Link to={`/app/leads/${p.lead_id}`}>
                                <Button size="sm" variant="ghost" data-testid={`ir-lead-${p.id}`}>
                                  Lead
                                </Button>
                              </Link>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
